import { ColorScheme, Theme } from "@/context/ThemeContext";
import { getLogger } from "@/utils/log";
import { getSystemColorScheme } from "@/utils/systemPreference";

const logger = getLogger("theme:utils");

/**
 * Common theme data and options
 */
export const themeData = {
  /**
   * Get standard theme options with labels
   */
  getThemeOptions: (): Array<{ id: Theme; label: string }> => [
    { id: Theme.EMERALD, label: "Emerald" },
    { id: Theme.PURPLE, label: "Purple" },
    { id: Theme.SKY, label: "Sky" },
    { id: Theme.AMBER, label: "Amber" },
  ],
};

/**
 * Theme preview management utilities
 */
export const themePreview = {
  /**
   * Creates preview handlers for theme components
   *
   * @example
   * ```tsx
   * const { handleMouseEnter, handleMouseLeave, cleanup } = React.useMemo(
   *   () => themePreview.createPreviewHandlers({
   *     previewCallback: previewTheme,
   *     previewDelay: 300,
   *     disablePreview: false
   *   }),
   *   [previewTheme, previewDelay, disablePreview]
   * );
   *
   * // Use in component
   * useEffect(() => () => cleanup(), [cleanup]);
   * ```
   */
  createPreviewHandlers: <T>({
    previewCallback,
    previewDelay = 300,
    disablePreview = false,
    timeoutFn = setTimeout,
    clearTimeoutFn = clearTimeout,
  }: {
    previewCallback: (value: T | null) => void;
    previewDelay: number;
    disablePreview?: boolean;
    timeoutFn?: typeof setTimeout;
    clearTimeoutFn?: typeof clearTimeout;
  }) => {
    const timerRef = { current: null as ReturnType<typeof setTimeout> | null };

    const clearPreviewTimer = () => {
      if (timerRef.current !== null) {
        clearTimeoutFn(timerRef.current);
        timerRef.current = null;
      }
    };

    return {
      cleanup: () => clearPreviewTimer(),
      handleMouseEnter: (value: T) => {
        if (disablePreview) return;
        clearPreviewTimer();
        try {
          timerRef.current = timeoutFn(() => {
            try {
              previewCallback(value);
            } catch (err) {
              logger.error(err, { context: "preview callback" });
            }
          }, previewDelay);
        } catch (err) {
          logger.error(err, { context: "setting preview timer" });
        }
      },
      handleMouseLeave: () => {
        clearPreviewTimer();
        try {
          previewCallback(null);
        } catch (err) {
          logger.error(err, { context: "preview callback mouse leave" });
        }
      },
      timerRef, // Expose timerRef for testing
    };
  },
};

/**
 * Theme color utilities for consistent color mapping
 */
export const themeColors = {
  /**
   * Get background color class for a theme
   */
  getColorIndicatorClass: ({ themeId }: { themeId: Theme }): string => {
    switch (themeId) {
      case Theme.SKY:
        return "bg-sky-500 dark:bg-sky-600";
      case Theme.EMERALD:
        return "bg-emerald-500 dark:bg-emerald-600";
      case Theme.AMBER:
        return "bg-amber-500 dark:bg-amber-600";
      case Theme.PURPLE:
        return "bg-purple-500 dark:bg-purple-600";
      default:
        return "bg-emerald-500 dark:bg-emerald-600";
    }
  },

  /**
   * Get ring color class for a theme
   */
  getRingColorClass: ({ themeId }: { themeId: Theme }): string => {
    switch (themeId) {
      case Theme.SKY:
        return "ring-sky-500 dark:ring-sky-400";
      case Theme.EMERALD:
        return "ring-emerald-500 dark:ring-emerald-400";
      case Theme.AMBER:
        return "ring-amber-500 dark:ring-amber-400";
      case Theme.PURPLE:
        return "ring-purple-500 dark:ring-purple-400";
      default:
        return "ring-emerald-500 dark:ring-emerald-400";
    }
  },

  /**
   * Get text color class for a theme
   */
  getTextColorClass: ({ themeId }: { themeId: Theme }): string => {
    switch (themeId) {
      case Theme.SKY:
        return "text-sky-500 dark:text-sky-400";
      case Theme.EMERALD:
        return "text-emerald-500 dark:text-emerald-400";
      case Theme.AMBER:
        return "text-amber-500 dark:text-amber-400";
      case Theme.PURPLE:
        return "text-purple-500 dark:text-purple-400";
      default:
        return "text-emerald-500 dark:text-emerald-400";
    }
  },
};

/**
 * Theme scheme utilities for consistent color scheme handling
 */
export const themeScheme = {
  /**
   * Get the actual color scheme class corresponding to a mode
   */
  getColorSchemeClasses: ({
    colorScheme,
  }: {
    colorScheme: ColorScheme;
  }): string => {
    switch (colorScheme) {
      case ColorScheme.DARK:
        return "bg-gray-900 text-gray-100";
      case ColorScheme.LIGHT:
        return "bg-white text-gray-900";
      case ColorScheme.SYSTEM:
        return getSystemColorScheme() === ColorScheme.DARK
          ? "bg-gray-900 text-gray-100"
          : "bg-white text-gray-900";
      default:
        return "bg-white text-gray-900";
    }
  },
  /**
   * Get the effective color scheme based on current state
   *
   * This handles the complexity of determining which color scheme to actually apply,
   * taking into account previews and system preferences.
   */
  getEffectiveColorScheme: ({
    colorScheme,
    previewColorSchemeValue,
    effectiveColorScheme,
    isUsingSystemPreference,
  }: {
    colorScheme: ColorScheme;
    previewColorSchemeValue: ColorScheme | null;
    effectiveColorScheme: ColorScheme;
    isUsingSystemPreference: boolean;
  }): ColorScheme => {
    // If previewing, use preview value
    if (previewColorSchemeValue) {
      return previewColorSchemeValue;
    }

    // Otherwise use system preference or selected scheme
    return isUsingSystemPreference ? effectiveColorScheme : colorScheme;
  },
};

/**
 * Card component style utilities
 */
export const cardStyles = {
  /**
   * Get hover classes for a card based on hover state and color scheme
   */
  getHoverClasses: ({
    hover,
    activeColorScheme,
  }: {
    hover: boolean;
    activeColorScheme: ColorScheme;
  }): string => {
    if (!hover) {
      return "focus-within:ring-2 focus-within:ring-theme-primary/30";
    }

    return activeColorScheme === ColorScheme.DARK
      ? "hover:shadow-xl hover:shadow-black/30 hover:border-gray-600 hover:translate-y-[-2px] focus-within:ring-2 focus-within:ring-theme-primary/30"
      : "hover:shadow-xl hover:shadow-gray-300/50 hover:border-gray-300 hover:translate-y-[-2px] focus-within:ring-2 focus-within:ring-theme-primary/30";
  },

  /**
   * Get opacity classes for a card based on opacity level and color scheme
   */
  getOpacityClasses: ({
    opacity,
    activeColorScheme,
  }: {
    opacity: string;
    activeColorScheme: ColorScheme;
  }): string => {
    const opacityMap: Record<string, Record<string, string>> = {
      heavy: {
        [ColorScheme.DARK]: "bg-opacity-100",
        [ColorScheme.LIGHT]: "bg-opacity-100",
      },
      light: {
        [ColorScheme.DARK]: "bg-opacity-90",
        [ColorScheme.LIGHT]: "bg-opacity-90",
      },
      medium: {
        [ColorScheme.DARK]: "bg-opacity-100",
        [ColorScheme.LIGHT]: "bg-opacity-100",
      },
      none: {
        [ColorScheme.DARK]: "",
        [ColorScheme.LIGHT]: "",
      },
    };

    // Safe access with fallback
    const opacityObj = opacityMap[opacity] || opacityMap.medium;
    return opacityObj[activeColorScheme] || opacityObj[ColorScheme.LIGHT];
  },
  /**
   * Get the appropriate variant classes for a card based on color scheme
   */
  getVariantClasses: ({
    variant,
    activeColorScheme,
  }: {
    variant: string;
    activeColorScheme: ColorScheme;
  }): string => {
    const variantMap: Record<string, Record<string, string>> = {
      default: {
        [ColorScheme.DARK]: "bg-gray-800 text-gray-100 border border-gray-700",
        [ColorScheme.LIGHT]: "bg-white text-theme-text border border-gray-200",
      },
      elevated: {
        [ColorScheme.DARK]:
          "bg-gray-800 text-gray-100 shadow-lg shadow-black/20 border border-gray-700",
        [ColorScheme.LIGHT]:
          "bg-white text-theme-text shadow-lg shadow-gray-200/50",
      },
      ghost: {
        [ColorScheme.DARK]: "bg-gray-800/30 text-gray-100",
        [ColorScheme.LIGHT]: "bg-gray-100/50 text-theme-text",
      },
      outline: {
        [ColorScheme.DARK]:
          "bg-gray-800/50 border-2 border-gray-600 text-gray-100",
        [ColorScheme.LIGHT]:
          "bg-white border-2 border-gray-300 text-theme-text",
      },
    };

    // Safe access with fallback
    const variantObj = variantMap[variant] || variantMap.default;
    return variantObj[activeColorScheme] || variantObj[ColorScheme.LIGHT];
  },
};

/**
 * Theme schema utilities for dropdown menus and interactive list items
 */
export const themeSchemaUtils = {
  /**
   * Get theme-aware hover effect classes
   */
  getHoverClasses: ({ themeId }: { themeId: Theme }): string => {
    switch (themeId) {
      case Theme.EMERALD:
        return "hover:bg-emerald-100 dark:hover:bg-emerald-900/40";
      case Theme.AMBER:
        return "hover:bg-amber-100 dark:hover:bg-amber-900/40";
      case Theme.SKY:
        return "hover:bg-sky-100 dark:hover:bg-sky-900/40";
      case Theme.PURPLE:
        return "hover:bg-purple-100 dark:hover:bg-purple-900/40";
      default:
        return "hover:bg-emerald-100 dark:hover:bg-emerald-900/40";
    }
  },
  /**
   * Get appropriate classes for theme menu items
   *
   * This is used for elements in theme dropdown menus and similar components
   * to maintain consistent styling across theme UI components.
   */
  getSchemeClasses: ({
    themeId,
    isActive,
  }: {
    themeId: Theme;
    isActive: boolean;
  }): string => {
    const baseClasses = "border-l-2";

    // For non-active items, we want a neutral look with hover effect
    if (!isActive) {
      let hoverClasses = "";

      switch (themeId) {
        case Theme.EMERALD:
          hoverClasses = "hover:bg-emerald-100 dark:hover:bg-emerald-900/40";
          break;
        case Theme.AMBER:
          hoverClasses = "hover:bg-amber-100 dark:hover:bg-amber-900/40";
          break;
        case Theme.SKY:
          hoverClasses = "hover:bg-sky-100 dark:hover:bg-sky-900/40";
          break;
        case Theme.PURPLE:
          hoverClasses = "hover:bg-purple-100 dark:hover:bg-purple-900/40";
          break;
        default:
          hoverClasses = "hover:bg-emerald-100 dark:hover:bg-emerald-900/40";
      }

      return `${baseClasses} border-l-transparent text-gray-700 dark:text-gray-200 ${hoverClasses}`;
    }

    // For active items, we want theme-colored styles
    let activeClasses = "";

    switch (themeId) {
      case Theme.EMERALD:
        activeClasses =
          "bg-emerald-200 dark:bg-emerald-900/20 border-l-emerald-500 text-emerald-600 dark:text-emerald-400";
        break;
      case Theme.AMBER:
        activeClasses =
          "bg-amber-200 dark:bg-amber-900/20 border-l-amber-500 text-amber-600 dark:text-amber-400";
        break;
      case Theme.SKY:
        activeClasses =
          "bg-sky-200 dark:bg-sky-900/20 border-l-sky-500 text-sky-600 dark:text-sky-400";
        break;
      case Theme.PURPLE:
        activeClasses =
          "bg-purple-200 dark:bg-purple-900/20 border-l-purple-500 text-purple-600 dark:text-purple-400";
        break;
      default:
        activeClasses =
          "bg-emerald-50 dark:bg-emerald-900/20 border-l-emerald-500 text-emerald-600 dark:text-emerald-400";
    }

    return `${baseClasses} ${activeClasses}`;
  },

  /**
   * Get ring color classes for theme selectors
   *
   * This is used for ring styles in selected theme items
   */
  getThemeRingClass: ({ themeId }: { themeId: Theme }): string => {
    switch (themeId) {
      case Theme.EMERALD:
        return "ring-white dark:ring-emerald-500/70";
      case Theme.AMBER:
        return "ring-white dark:ring-amber-500/70";
      case Theme.SKY:
        return "ring-white dark:ring-sky-500/70";
      case Theme.PURPLE:
        return "ring-white dark:ring-purple-500/70";
      default:
        return "ring-white dark:ring-gray-400";
    }
  },
};

/**
 * Common component styling for theme-related components
 */
export const themeComponentStyles = {
  /**
   * Get common container styling for theme component containers
   *
   * @param withExtraPadding - Add extra padding (default: false)
   * @param isSchemeToggleGroup - Use lighter bg for scheme toggle group (default: false)
   */
  getContainerClasses: ({
    withExtraPadding = false,
    isSchemeToggleGroup = false,
  }: {
    withExtraPadding?: boolean;
    isSchemeToggleGroup?: boolean;
  } = {}): string => {
    // Scheme toggle group has a special lighter background in light mode
    const bgClasses = isSchemeToggleGroup
      ? "bg-gray-50/95 dark:bg-gray-800/95 border-gray-200 dark:border-gray-700"
      : "bg-white/95 dark:bg-gray-800/95 border-gray-100 dark:border-gray-700";

    const baseClasses = `h-10 rounded-md ${bgClasses} border shadow-[0_1px_2px_0_rgba(0,0,0,0.03)] dark:shadow-sm`;

    return withExtraPadding ? `${baseClasses} px-2.5` : baseClasses;
  },

  /**
   * Get common button/control styling
   */
  getControlClasses: (): string =>
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary/40 dark:focus-visible:ring-theme-primary/40",
};
